import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "smart-forms",
  kind: "horizontal",
  title: "Smart Form & Procedural Workflow Systems",
  shortTitle: "Smart Forms",
  status: "draft",
  tier: "experience",
  seoTitle: "Smart Form & Procedural Workflow Systems | General Applications",
  seoDescription:
    "Step-gated procedural forms with conditional logic, captured sign-off, and structured records suitable for audit and traceability.",
  summary:
    "Forms that carry a procedure rather than just collect fields: conditional step gating, required readings and sign-off captured in sequence, and a structured record afterwards that holds up when someone asks what was done and in what order.",
  image: "/images/blog/operational-task-management-hero.webp",
  relatedCapabilitySlugs: [
    "automation-operations",
    "software-architecture-engineering",
  ],
  relatedIndustrySlugs: [
    "military",
    "healthcare",
    "energy",
    "enterprise",
    "agriculture",
  ],
  relatedPostSlugs: [
    "operational-task-management",
    "no-happy-path",
    "schemas-first-parsers-last",
    "smart-forms-for-harsh-environments",
  ],
  engineering: [
    "Step-gated forms with conditional paths, required readings, and captured acknowledgment",
    "Structured records for work execution, inspection, maintenance, approval, and exception handling",
    "Role, authority, and status models that prevent a form from becoming an unowned checklist",
    "Validation, calculation, attachment, and evidence capture tied to procedural context",
    "Workflow state, sign-off, change, and traceability design",
    "Interfaces that support operators during work rather than reconstructing it afterward",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "A Form Can Carry the Work",
    },
    {
      type: "paragraph",
      text: "A generic digital form records fields. A smart form carries the state of a procedure: what has happened, what remains conditional, what must be checked, who has authority to continue, and what evidence belongs with the result. That makes it useful when a work item has more consequence than a simple submission.",
    },
    {
      type: "heading",
      level: 2,
      text: "The Exception Is Part of the Design",
    },
    {
      type: "paragraph",
      text: "The useful path is not always a clean sequence of required fields. A procedure needs an explicit way to hold, branch, reject, defer, correct, and resume work without losing the reason or the record. Designing those states first keeps an interface from silently forcing operators back into side conversations and spreadsheets.",
    },
  ],
  phases: {
    study: {
      focus:
        "Map the actual procedure, authority, information, exceptions, and record obligations before recreating it as a screen.",
      produces: [
        "Current procedure, role, and handoff model",
        "Required data, evidence, approval, and retention inventory",
        "Exception, hold, correction, and escalation path analysis",
        "Form scope and system-integration boundary",
      ],
      fit: {
        good: [
          "Operational work that depends on sequence, conditions, readings, sign-off, or a defensible record",
          "Teams replacing paper forms, spreadsheets, or disconnected approval steps with a shared workflow",
        ],
        poor: [
          "A simple one-time survey or intake form with no procedure, state, or accountability behind it",
          "A process whose ownership and approval authority are undefined",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the procedure state model, data structure, authority boundaries, and evidence requirements before styling the interface.",
      produces: [
        "Workflow state and transition model",
        "Form, record, attachment, and evidence data model",
        "Role, permission, approval, and notification design",
        "Integration and record-retention approach",
      ],
    },
    "detailed-design": {
      focus:
        "Specify each path, condition, validation rule, calculation, and recovery behavior that the form must support in real work.",
      produces: [
        "Screen flows and conditional interaction specifications",
        "Validation, calculation, and required-reading rules",
        "Hold, rejection, correction, and resumption procedures",
        "Evidence, audit-trail, and reporting definitions",
      ],
    },
    implementation: {
      focus:
        "Build the workflow with the people who perform it, then validate it against normal work and the exceptions that expose missing state.",
      produces: [
        "Working smart-form workflow and structured records",
        "Role-aware approvals, evidence capture, and traceability",
        "Validated exception and recovery paths",
        "Operational guidance for adoption and continued revision",
      ],
    },
  },
};
